import { useEffect, useState, useRef, RefObject } from 'react';

interface UseNestedTableMaxHeightOptions {
  /** Ref to the parent/outer table container element */
  parentTableRef?: RefObject<any>;
  /** Additional padding/margin to subtract (in pixels) */
  additionalOffset?: number;
  /** Enable/disable the effect */
  enabled?: boolean;
}

export function useNestedTableMaxHeight(
  options: UseNestedTableMaxHeightOptions = {}
) {
  const { parentTableRef, additionalOffset = 0, enabled = true } = options;
  
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const nestedContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const calculateMaxHeight = () => {
      // If we have a parent table ref, use that to find the container
      let outerTableContainer: HTMLElement | null = null;
      
      if (parentTableRef?.current) {
        // Extract the actual DOM element from the ref
        let parentElement: HTMLElement | null = null;
        
        // Try different ways to get the DOM element
        if (parentTableRef.current instanceof HTMLElement) {
          // It's already an HTMLElement
          parentElement = parentTableRef.current;
        } else if (parentTableRef.current.containerRef?.current) {
          // It's a custom ref with containerRef property
          parentElement = parentTableRef.current.containerRef.current;
        } else if (parentTableRef.current.current instanceof HTMLElement) {
          // It's a nested ref
          parentElement = parentTableRef.current.current;
        }
        
        // Now safely use DOM methods
        if (parentElement instanceof HTMLElement) {
          outerTableContainer = parentElement.closest('.tableContainer') || 
                              parentElement.querySelector('.tableContainer');
        }
      }
      
      // Fallback: traverse up from nested container to find outer table
      if (!outerTableContainer && nestedContainerRef.current) {
        // Find all .tableContainer elements
        const allTableContainers = document.querySelectorAll('.tableContainer');
        
        // Find the one that contains our nested container but isn't our own
        for (let i = 0; i < allTableContainers.length; i++) {
          const container = allTableContainers[i] as HTMLElement;
          if (container.contains(nestedContainerRef.current) && 
              !nestedContainerRef.current.contains(container)) {
            // This container contains us but we don't contain it = it's our parent
            outerTableContainer = container;
            break;
          }
        }
      }

      if (!outerTableContainer) {
        // Can't calculate, use viewport height as fallback
        setMaxHeight(window.innerHeight * 0.6); // 60% of viewport
        return;
      }

      // Get the outer table container's height
      const outerRect = outerTableContainer.getBoundingClientRect();
      const outerHeight = outerRect.height;

      // Find and measure the outer thead
      const outerThead = outerTableContainer.querySelector('thead');
      const outerTheadHeight = outerThead?.getBoundingClientRect().height || 0;

      // Find and measure the outer pagination toolbar
      // Look for pagination in the parent of the table container
      let outerPaginationHeight = 0;
      const tableWrapper = outerTableContainer.parentElement;
      if (tableWrapper) {
        const pagination = tableWrapper.querySelector('.paginationToolbar');
        outerPaginationHeight = pagination?.getBoundingClientRect().height || 0;
      }

      // Calculate available height
      // Subtract: thead height, pagination height, any additional offset, and some padding
      const availableHeight = outerHeight - outerTheadHeight - outerPaginationHeight - additionalOffset - 16;

      // Ensure we have a minimum reasonable height
      const finalHeight = Math.max(200, availableHeight);
      
      setMaxHeight(finalHeight);
    };

    // Initial calculation
    calculateMaxHeight();

    // Recalculate on window resize
    const handleResize = () => {
      calculateMaxHeight();
    };
    window.addEventListener('resize', handleResize);

    // Use ResizeObserver to watch for changes in the parent container
    let resizeObserver: ResizeObserver | null = null;
    if (parentTableRef?.current) {
      // Extract the actual DOM element
      let parentElement: HTMLElement | null = null;
      
      if (parentTableRef.current instanceof HTMLElement) {
        parentElement = parentTableRef.current;
      } else if (parentTableRef.current.containerRef?.current) {
        parentElement = parentTableRef.current.containerRef.current;
      } else if (parentTableRef.current.current instanceof HTMLElement) {
        parentElement = parentTableRef.current.current;
      }
      
      if (parentElement instanceof HTMLElement) {
        const outerTableContainer = parentElement.closest('.tableContainer') || 
                                   parentElement.querySelector('.tableContainer');
        
        if (outerTableContainer) {
          resizeObserver = new ResizeObserver(calculateMaxHeight);
          resizeObserver.observe(outerTableContainer);
        }
      }
    }

    // Also watch for changes in the nested container's ancestors
    const mutationObserver = new MutationObserver(calculateMaxHeight);
    if (nestedContainerRef.current?.parentElement) {
      mutationObserver.observe(nestedContainerRef.current.parentElement, {
        childList: true,
        subtree: false,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled, parentTableRef, additionalOffset]);

  return {
    nestedContainerRef,
    maxHeight,
  };
}
